import React, { useEffect, useState, useCallback } from "react";
import { Card, Badge, Button, Modal, Table } from "react-bootstrap";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { useLanguage } from "../contexts/LanguageContext";
import th   from "../locales/islamic/th";
import jawi from "../locales/islamic/jawi";
import Swal from "sweetalert2";

export default function IslamicManageTeaching() {
  const { setLoading } = useLoading();
  const { lang } = useLanguage();
  const t = lang === 'th' ? th : jawi;

  const [yearId, setYearId] = useState(() => localStorage.getItem("yearId") || null);
  const [yearList, setYearList] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filterClass, setFilterClass] = useState("");
  const [filterGender, setFilterGender] = useState("");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [teachings, setTeachings] = useState([]);

  const [show, setShow] = useState(false);
  const [subjectsAvail, setSubjectsAvail] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [formSubject, setFormSubject] = useState("");
  const [formTeacher, setFormTeacher] = useState("");

  const [showPeriod, setShowPeriod] = useState(false);
  const [selectedTeaching, setSelectedTeaching] = useState(null);
  const [slots, setSlots] = useState([]);
  const [periodList, setPeriodList] = useState([]);
  const [formDay, setFormDay] = useState("");
  const [formPeriod, setFormPeriod] = useState("");

  useEffect(() => {
    axiosInstance.get(`${process.env.REACT_APP_API}/getStudyYear`)
      .then(res => {
        const list = res.data;
        setYearList(list);
        let id = localStorage.getItem("yearId");
        if (!id && list.length > 0) { id = list[0].year_id; localStorage.setItem("yearId", id); }
        setYearId(id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-assessment/rooms/${yearId}`)
      .then(res => { setRooms(res.data); setSelectedRoom(null); setTeachings([]); })
      .catch(() => setRooms([]))
      .finally(() => setLoading(false));
  }, [yearId, setLoading]);

  const loadTeachings = useCallback((ir_id) => {
    setLoading(true);
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/teachingsInRoom/${ir_id}`)
      .then(res => setTeachings(res.data))
      .catch(() => setTeachings([]))
      .finally(() => setLoading(false));
  }, [setLoading]);

  const handleSelectRoom = (room) => {
    setSelectedRoom(room);
    loadTeachings(room.ir_id);
  };

  const handleOpenAdd = () => {
    if (!selectedRoom) return;
    setFormSubject("");
    setFormTeacher("");
    Promise.all([
      axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/subjectsWithoutTeacher/${selectedRoom.ir_id}`),
      axiosInstance.get(`${process.env.REACT_APP_API}/getTeacherList?side=islamic`)
    ])
      .then(([subRes, teachRes]) => {
        setSubjectsAvail(subRes.data);
        setTeacherList(teachRes.data);
        setShow(true);
      })
      .catch(() => {});
  };

  const handleAdd = () => {
    if (!formSubject || !formTeacher) return;
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/islamic-teaching/addTeaching`, {
      ir_id: selectedRoom.ir_id,
      isubject_id: Number(formSubject),
      t_id: Number(formTeacher)
    })
      .then(() => { setShow(false); loadTeachings(selectedRoom.ir_id); })
      .catch(err => Swal.fire({ icon: 'error', text: err.response?.data?.message || t.manageTeaching.errorShort }))
      .finally(() => setLoading(false));
  };

  const handleDelete = (is_teachId) => {
    Swal.fire({
      title: t.manageTeaching.confirmDeleteTitle,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t.manageTeaching.confirmDeleteBtn,
      cancelButtonText: t.manageTeaching.cancelDeleteBtn,
      confirmButtonColor: '#d33'
    }).then(result => {
      if (!result.isConfirmed) return;
      setLoading(true);
      axiosInstance.post(`${process.env.REACT_APP_API}/islamic-teaching/deleteTeaching/${is_teachId}`)
        .then(() => loadTeachings(selectedRoom.ir_id))
        .catch(err => Swal.fire({ icon: 'error', text: err.response?.data?.message || t.manageTeaching.deleteFailMsg }))
        .finally(() => setLoading(false));
    });
  };

  const loadSlots = useCallback((is_teachId) => {
    axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/teachtimeSlots/${is_teachId}`)
      .then(res => setSlots(res.data))
      .catch(() => setSlots([]));
  }, []);

  const handleOpenPeriod = (teaching) => {
    setSelectedTeaching(teaching);
    setFormDay("");
    setFormPeriod("");
    Promise.all([
      axiosInstance.get(`${process.env.REACT_APP_API}/islamic-teaching/teachtimeSlots/${teaching.is_teachId}`),
      axiosInstance.get(`${process.env.REACT_APP_API}/getPeriodList/${yearId}`)
    ])
      .then(([slotRes, periodRes]) => {
        setSlots(slotRes.data);
        setPeriodList(periodRes.data);
        setShowPeriod(true);
      })
      .catch(() => {});
  };

  const handleAddSlot = () => {
    if (formDay === "" || !formPeriod) return;
    setLoading(true);
    axiosInstance.post(`${process.env.REACT_APP_API}/islamic-teaching/addTeachtime`, {
      is_teachId: selectedTeaching.is_teachId,
      period_id: Number(formPeriod),
      teach_day: Number(formDay)
    })
      .then(() => {
        setFormDay("");
        setFormPeriod("");
        loadSlots(selectedTeaching.is_teachId);
        loadTeachings(selectedRoom.ir_id);
      })
      .catch(err => {
        const code = err.response?.data?.code;
        Swal.fire({
          icon: 'error',
          text: code === 'DUPLICATE' ? t.manageTeaching.duplicatePeriod : (err.response?.data?.message || t.manageTeaching.errorShort),
        });
      })
      .finally(() => setLoading(false));
  };

  const handleDeleteSlot = (is_teachtime_id) => {
    setLoading(true);
    axiosInstance.delete(`${process.env.REACT_APP_API}/islamic-teaching/deleteTeachtime/${is_teachtime_id}`)
      .then(() => {
        loadSlots(selectedTeaching.is_teachId);
        loadTeachings(selectedRoom.ir_id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const renderSlotSummary = (slot_summary) => {
    if (!slot_summary) return <span className="text-muted small">—</span>;
    const groups = {};
    slot_summary.split(',').forEach(pair => {
      const [d, p] = pair.split(':');
      if (!groups[d]) groups[d] = [];
      groups[d].push(p);
    });
    return (
      <div className="d-flex flex-wrap gap-1">
        {Object.entries(groups).map(([day, periods]) => (
          <span key={day} className="badge bg-light text-dark border small">
            {t.days[Number(day)]} : {periods.join(', ')}
          </span>
        ))}
      </div>
    );
  };

  const fmt = (time) => time ? String(time).substring(0, 5) : '';
  const genderLabel = (g) => g === 'm' ? t.common.male : t.common.female;

  const classList = [...new Set(rooms.map(r => r.ic_class))].sort();
  const filteredRooms = rooms.filter(r =>
    (!filterClass  || r.ic_class   === filterClass) &&
    (!filterGender || r.ir_gender  === filterGender)
  );

  return (
    <React.Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="main-title mb-0">{t.manageTeaching.pageTitle}</h4>
        <select
          className="form-select w-auto"
          value={yearId || ""}
          onChange={e => { localStorage.setItem("yearId", e.target.value); setYearId(e.target.value); }}
        >
          {yearList.map(y => (
            <option key={y.year_id} value={y.year_id}>{t.common.studyYear(y.year_year)}</option>
          ))}
        </select>
      </div>

      <div className="row g-3">
        <div className="col-md-3">
          <Card className="card-one">
            <Card.Header><span className="fw-semibold">{t.manageTeaching.classRoom}</span></Card.Header>
            <Card.Body className="p-2">
              <div className="d-flex gap-1 mb-2">
                <select
                  className="form-select form-select-sm"
                  value={filterClass}
                  onChange={e => setFilterClass(e.target.value)}
                >
                  <option value="">{t.manageTeaching.filterClass}</option>
                  {classList.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  value={filterGender}
                  onChange={e => setFilterGender(e.target.value)}
                >
                  <option value="">{t.manageTeaching.filterGender}</option>
                  <option value="m">{t.common.male}</option>
                  <option value="f">{t.common.female}</option>
                </select>
              </div>
              {filteredRooms.length === 0 ? (
                <p className="text-secondary text-center py-3 small">{t.manageTeaching.noRooms}</p>
              ) : (
                <div className="d-grid gap-1">
                  {filteredRooms.map(r => (
                    <button
                      key={r.ir_id}
                      className={`btn btn-sm text-start ${selectedRoom?.ir_id === r.ir_id ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => handleSelectRoom(r)}
                    >
                      <span className="fw-semibold">{r.ic_class}/{r.ir_no}</span>
                      <span className="ms-2 small">{r.is_programCode}</span>
                      <Badge bg={r.ir_gender === "m" ? "info" : "danger"} className="ms-1 small">
                        {genderLabel(r.ir_gender)}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-9">
          {!selectedRoom ? (
            <Card className="card-one">
              <Card.Body className="text-center text-secondary py-5">
                <i className="ri-arrow-left-line fs-3 d-block mb-2"></i>
                {t.manageTeaching.selectRoom}
              </Card.Body>
            </Card>
          ) : (
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center gap-2">
                <span className="fw-semibold">{t.common.classNo(selectedRoom.ic_class, selectedRoom.ir_no)}</span>
                <Badge bg="info">{selectedRoom.is_programCode}</Badge>
                <Badge bg={selectedRoom.ir_gender === "m" ? "primary" : "danger"}>{genderLabel(selectedRoom.ir_gender)}</Badge>
                <Button size="sm" variant="success" className="ms-auto" onClick={handleOpenAdd}>
                  <i className="ri-add-line me-1"></i>{t.manageTeaching.addTeachingBtn}
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {teachings.length === 0 ? (
                  <p className="text-center text-secondary py-4">{t.manageTeaching.noTeachings}</p>
                ) : (
                  <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-secondary">
                      <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th style={{ width: 90 }}>{t.manageTeaching.colCode}</th>
                        <th>{t.manageTeaching.colSubjectName}</th>
                        <th>{t.manageTeaching.colTeacher}</th>
                        <th>{t.manageTeaching.colDayPeriod}</th>
                        <th style={{ width: 90 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {teachings.map((teaching, idx) => (
                        <tr key={teaching.is_teachId}>
                          <td className="text-secondary align-middle">{idx + 1}</td>
                          <td className="align-middle">{teaching.isubject_isid}</td>
                          <td className="align-middle arabic-text">{teaching.isubject_name}</td>
                          <td className="align-middle">{teaching.t_name || <span className="text-muted">-</span>}</td>
                          <td className="align-middle">{renderSlotSummary(teaching.slot_summary)}</td>
                          <td className="align-middle text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <Button size="sm" variant="outline-primary" title={t.manageTeaching.assignPeriodTitle} onClick={() => handleOpenPeriod(teaching)}>
                                <i className="ri-calendar-2-line"></i>
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDelete(teaching.is_teachId)}>
                                <i className="ri-delete-bin-line"></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* Add Teaching Modal */}
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t.manageTeaching.addTeachingModalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-semibold">{t.manageTeaching.labelSubject}</label>
            <select className="form-select" value={formSubject} onChange={e => setFormSubject(e.target.value)}>
              <option value="">{t.manageTeaching.subjectPlaceholder}</option>
              {subjectsAvail.map(s => (
                <option key={s.isubject_id} value={s.isubject_id}>
                  {s.isubject_isid} — {s.isubject_name}
                </option>
              ))}
            </select>
            {subjectsAvail.length === 0 && (
              <div className="form-text text-warning">{t.manageTeaching.allSubjectsAssigned}</div>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label fw-semibold">{t.manageTeaching.labelTeacher}</label>
            <select className="form-select" value={formTeacher} onChange={e => setFormTeacher(e.target.value)}>
              <option value="">{t.manageTeaching.teacherPlaceholder}</option>
              {teacherList.map(tc => (
                <option key={tc.t_id} value={tc.t_id}>{tc.t_arabicname ? `${tc.t_arabicname} — ${tc.t_name}` : tc.t_name}</option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>{t.common.cancel}</Button>
          <Button variant="success" onClick={handleAdd} disabled={!formSubject || !formTeacher}>
            <i className="ri-save-line me-1"></i>{t.common.save}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Period Modal */}
      <Modal show={showPeriod} onHide={() => setShowPeriod(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {t.manageTeaching.assignPeriodTitle} —{" "}
            <span className="arabic-text text-secondary">
              {selectedTeaching?.isubject_isid} {selectedTeaching?.isubject_name}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 align-items-end mb-3">
            <div style={{ flex: 1 }}>
              <label className="form-label fw-semibold mb-1">{t.manageTeaching.labelDay}</label>
              <select className="form-select" value={formDay} onChange={e => setFormDay(e.target.value)}>
                <option value="">{t.manageTeaching.dayPlaceholder}</option>
                {t.days.map((name, idx) => (
                  <option key={idx} value={idx}>{name}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="form-label fw-semibold mb-1">{t.manageTeaching.labelPeriod}</label>
              <select className="form-select" value={formPeriod} onChange={e => setFormPeriod(e.target.value)}>
                <option value="">{t.manageTeaching.periodPlaceholder}</option>
                {periodList.map(p => (
                  <option key={p.period_id} value={p.period_id}>
                    {p.period_order} — {fmt(p.period_begin)}–{fmt(p.period_end)}
                  </option>
                ))}
              </select>
            </div>
            <Button variant="success" disabled={formDay === "" || !formPeriod} onClick={handleAddSlot}>
              <i className="ri-add-line me-1"></i>{t.common.add}
            </Button>
          </div>

          {slots.length === 0 ? (
            <p className="text-center text-secondary py-3">{t.manageTeaching.noPeriods}</p>
          ) : (
            <Table bordered hover size="sm" className="mb-0">
              <thead className="table-secondary">
                <tr>
                  <th style={{ width: 50 }} className="text-center">#</th>
                  <th>{t.manageTeaching.labelDay}</th>
                  <th style={{ width: 70 }} className="text-center">{t.manageTeaching.labelPeriod}</th>
                  <th style={{ width: 130 }}>{t.attendance.colTime}</th>
                  <th style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {slots
                  .slice()
                  .sort((a, b) => a.teach_day - b.teach_day || a.period_order - b.period_order)
                  .map((s, idx) => (
                    <tr key={s.is_teachtime_id}>
                      <td className="text-center text-secondary align-middle">{idx + 1}</td>
                      <td className="align-middle">{t.days[s.teach_day]}</td>
                      <td className="text-center align-middle fw-semibold">{s.period_order}</td>
                      <td className="align-middle text-secondary small">{fmt(s.period_begin)} – {fmt(s.period_end)}</td>
                      <td className="text-center align-middle">
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteSlot(s.is_teachtime_id)}>
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPeriod(false)}>{t.common.close}</Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
